import * as React from "react";
import { SheetContent } from "../sheet";
import { cn } from "../../lib/utils";

/**
 * Shared layout for hub right sheets (Sheet side="right").
 * Widths: CSS variables in globals.css (--right-panel-max-*); size maps to those.
 */
const SIZE_TO_MAX_CLASS = {
  default: "sm:max-w-[var(--right-panel-max-default)]",
  wide: "sm:max-w-[var(--right-panel-max-wide)]",
  extraWide: "sm:max-w-[var(--right-panel-max-extra-wide)]",
};

const RIGHT_PANEL_CONTENT_BASE =
  "flex h-full max-h-[100dvh] w-full flex-col gap-0 p-0";

/** Header strip: title row + optional description (hub sheets). */
export const rightPanelHeaderClassName =
  "border-b border-border px-6 py-4 text-left shrink-0";

/** Scrollable region between header and footer; add `space-y-*` on this node or an inner wrapper. */
export const rightPanelBodyScrollClassName =
  "flex-1 overflow-y-auto px-6 py-4 min-h-0";

/** Sticky footer strip (actions). */
export const rightPanelFooterClassName =
  "border-t border-border px-6 py-4 shrink-0";

/**
 * Merge default right-panel SheetContent classes. Use when you need `SheetContent` directly.
 *
 * @param {{ size?: 'default' | 'wide' | 'extraWide'; className?: string }} [options]
 */
export function rightPanelSheetContentClassName(options = {}) {
  const { size = "default", className } = options;
  return cn(
    RIGHT_PANEL_CONTENT_BASE,
    SIZE_TO_MAX_CLASS[size] ?? SIZE_TO_MAX_CLASS.default,
    className,
  );
}

/**
 * Right `SheetContent` with hub defaults: overlay, slide-in animation, shared max-width.
 * Pass `className` to override or extend (e.g. `overflow-y-auto` for single-scroll preview panels).
 *
 * @param {{ size?: 'default' | 'wide' | 'extraWide' } & React.ComponentPropsWithoutRef<typeof SheetContent>} props
 */
export const RightPanelSheetContent = React.forwardRef(
  ({ className, size = "default", showOverlay = true, ...props }, ref) => (
    <SheetContent
      ref={ref}
      side="right"
      showOverlay={showOverlay}
      className={rightPanelSheetContentClassName({ size, className })}
      {...props}
    />
  ),
);
RightPanelSheetContent.displayName = "RightPanelSheetContent";
